<template>
  <tooltip-wrapper
    :tooltip="tooltipText"
    :placement="placement"
  >
    <div
      data-component="base-action-icon-button"
      :class="componentState"
      @click="onClick"
    >
      <or-progress
        v-if="isLoading"
        type="circle"
        loading
      />

      <or-icon
        v-if="icon && !isLoading"
        :icon="icon"
      />

      <div
        v-if="content && !isLoading"
        class="content"
      >
        {{ content }}
      </div>
    </div>
  </tooltip-wrapper>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue-demi';
import {
  OrIconV3 as OrIcon,
  OrProgressV3 as OrProgress,
} from '@onereach/ui-components-25';

import TooltipWrapper from './TooltipWrapper.vue';
import { SIZE_PROP } from '../../constants';

// @TODO rename base-icon-button in AppChatbox module
export default defineComponent({
  name: 'BaseIconButton',
  components: {
    OrIcon,
    OrProgress,
    TooltipWrapper,
  },
  props: {
    icon: {
      type: String,
      default: '',
    },
    content: {
      type: String,
      default: '',
    },
    color: {
      type: String as PropType<'primary' | 'error' | 'neutral'>,
      default: 'neutral',
    },
    tooltip: {
      type: String,
      default: '',
    },
    placement: {
      type: String,
      default: 'bottom',
    },
    size: SIZE_PROP,
    isActive: Boolean,
    isLoading: Boolean,
    isDisabled: Boolean,
  },
  emits: ['click'],
  computed: {
    componentState() {
      return {
        'is-active': this.isActive || this.isLoading,
        'is-loading': this.isLoading,
        'is-disabled': this.isDisabled,
        [`is-${this.size}`]: true,
        [`color-${this.color}`]: true,
      };
    },
    tooltipText() {
      return this.size !== 'mobile' ? this.tooltip : '';
    },
  },
  methods: {
    onClick(event: MouseEvent) {
      if (!this.isLoading && !this.isDisabled) {
        this.$emit('click', event);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
[data-component='base-action-icon-button'] {
  --button-size: 2rem;
  --progress-size: 1.25rem;

  --icon-color: var(--or-c-inverse-on-surface);
  --icon-color-active: var(--or-c-inverse-surface);
  --background-color: var(--or-c-on-surface-variant);
  --background-hover-color: var(--or-c-inverse-on-surface);
  --background-active-color: var(--or-c-inverse-on-surface);

  &.color-primary {
    --icon-color: var(--or-c-on-background);
    --icon-color-active: var(--or-c-white);
    --background-color: var(--or-c-primary-opacity-0-12);
    --background-hover-color: var(--or-c-primary-hover);
    --background-active-color: var(--or-c-primary);
  }

  &.color-error {
    --icon-color: var(--or-c-on-error);
    --icon-color-active: var(--or-c-on-error);
    --background-color: var(--or-c-error);
    --background-hover-color: var(--or-c-error-hover);
    --background-active-color: var(--or-c-error-hover);
  }

  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  height: var(--button-size);
  width: var(--button-size);
  border-radius: 50%;

  background: var(--background-color);
  transition-property: background;
  transition-timing-function: var(--t-func);
  transition-duration: var(--t-duration);
  user-select: none;

  &.is-mobile {
    --button-size: 3.5rem;
    --progress-size: 1.75rem;

    .content,
    .or-icon-v3 {
      font-size: 2rem;
    }
  }

  &:not(.is-loading):not(.is-disabled) {
    cursor: pointer;
  }

  .content {
    font-size: var(--fs-1);
  }

  .content,
  .or-icon-v3 {
    color: var(--icon-color);
  }

  .or-progress-v3{
    ::v-deep svg {
      height: var(--progress-size);
      width: var(--progress-size);
    }

    ::v-deep circle:not([stroke-dasharray]) {
      stroke: transparent !important;
    }

    ::v-deep circle[stroke-dasharray] {
      stroke: var(--icon-color-active) !important;
    }
  }

  &.is-active {
    background: var(--background-active-color);
  }

  &:hover:not(.is-loading):not(.is-disabled) {
    background: var(--background-hover-color);
  }

  &.is-active,
  &:hover:not(.is-loading):not(.is-disabled) {
    .content,
    .or-icon-v3 {
      color: var(--icon-color-active);
    }
  }
}
</style>
